import { useHistory } from "react-router-dom";

import { dateTimeService } from "../date/DateTimeService";
import { urlBuilderService } from "../url/UrlBuilderService";
import { urlParserService } from "../url/UrlParserService";

type History = ReturnType<typeof useHistory>;

export interface ListFiltersService<T> {
    saveFilters(pathName: string, history: History, filters: T): void;
    getSavedFilters(locationSearch: string): T;
}

export const listFiltersService = <T>(defaultFilters: T): ListFiltersService<T> => {
    const urlBuilder = urlBuilderService();
    const urlParser = urlParserService();
    const dateService = dateTimeService();

    const saveFilters = (pathName: string, history: History, filters: T): void => {
        const filtersDiff = getDiffWithDefaultFilters(filters);

        const newUrl = urlBuilder.buildUrl(pathName, filtersDiff);

        history.replace(newUrl);
    };

    const getSavedFilters = (locationSearch: string): T => {
        const urlParams = urlParser.parse(locationSearch);

        if (!urlParams) {
            return defaultFilters;
        }

        const savedFilters = {};

        for (const key of Object.keys(defaultFilters)) {
            savedFilters[key] = getFilter(urlParams, key);
        }

        return savedFilters as T;
    };

    const getFilter = (urlParams: string | number | Date | Record<string, unknown>, key: string) => {
        if (dateService.isUTCDate(urlParams[key])) {
            return new Date(urlParams[key]);
        }

        const shouldConvertToNumber = !isNaN(Number(urlParams[key]));

        if (shouldConvertToNumber) {
            return Number(urlParams[key]);
        }

        if (urlParams[key] === "true") {
            return true;
        }

        if (urlParams[key] === "false") {
            return false;
        }

        if (typeof urlParams[key] === "object") {
            return Object.keys(urlParams[key]).map((nestedKey: string) => getFilter(urlParams[key], nestedKey));
        }

        return urlParams[key] || defaultFilters[key];
    };

    const getDiffWithDefaultFilters = (currentFilters: T) => {
        const keys = Object.keys(defaultFilters);

        const diffBetweenFilters = {};

        keys.forEach((key: string) => {
            if (currentFilters[key] instanceof Date) {
                diffBetweenFilters[key] = (currentFilters[key] as Date).toISOString();
            } else if (currentFilters[key] !== defaultFilters[key]) {
                diffBetweenFilters[key] = currentFilters[key];
            }
        });

        return diffBetweenFilters;
    };

    return {
        getSavedFilters,
        saveFilters
    };
};
